import React from 'react'
import Paper from '@mui/material/Paper'
import { Link, useLoaderData } from 'react-router-dom'
import { formatDateYYYYMMDDhhmmss } from '../../../../packages/utils/numberFormatters'
import Grid from '@mui/material/Grid'
import { CompanyPressReleases } from './CompanyPressReleases'
import { Helmet } from '@dr.pogodin/react-helmet'
import { ClockIcon, FileIcon } from 'lucide-react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemText from '@mui/material/ListItemText'
import { PressReleaseRelatedTransactionsTags } from '../../../../components/entities/PressReleaseRelatedTransactionsTags'
import { getPressReleaseLink } from '../../../../utils/get-press-release-link'
import { flagPath } from '../../../../components/entities/CompaniesGrid'
import { RelatedTransactions } from './RelatedTransactions'
import { PressReleaseEntity } from '@finance-lab/utils/types'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Typography from '@mui/material/Typography'

const PaperWrapper = ({ children }: { children: React.ReactNode }) => {
    return <Paper sx={{ padding: 4, maxWidth: '100%', img: { maxWidth: '100%' } }}>{children}</Paper>
}

const mapLanguageToCountry = (lang: string) => {
    switch (lang) {
        case 'en':
            return 'gb'
        case 'sv':
            return 'se'
        case 'da':
            return 'dk'
        case 'ja':
            return 'jp'
        case 'zh':
            return 'cn'
        case 'ko':
            return 'kr'
        default:
            return lang
    }
}

export const PressRelease = () => {
    const pressRelease = useLoaderData<PressReleaseEntity>()

    const bodyContent = pressRelease.body
    const publishDate = pressRelease.publishedAt
    const attachments = pressRelease.attachments.map((a) => ({
        ...a,
        caption: a.caption ?? a.url.split('/').pop(),
    }))

    return (
        <>
            <Helmet title={pressRelease.title} />
            <Grid container spacing={4}>
                <Grid
                    size={{ md: 8 }}
                    sx={{
                        justifyItems: 'center',
                    }}
                >
                    <PaperWrapper>
                        <Box sx={{ display: 'flex', maxWidth: '100%' }}>
                            <Breadcrumbs
                                aria-label='breadcrumb'
                                sx={{
                                    width: 'calc(100%)',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    ol: {
                                        flexWrap: 'nowrap',
                                        'li:last-of-type': {
                                            overflow: 'hidden',
                                        },
                                    },
                                }}
                            >
                                <Link color='inherit' to='/companies'>
                                    Bolag
                                </Link>
                                <Link color='inherit' to={`/companies/${pressRelease.companyId}`}>
                                    {pressRelease.companyName}
                                </Link>
                                <Typography
                                    sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden' }}
                                >
                                    {pressRelease.title}
                                </Typography>
                            </Breadcrumbs>
                            <Box sx={{ flexGrow: 1 }}>
                                <List sx={{ margin: 0, padding: 0, textAlign: 'right', display: 'flex' }}>
                                    {pressRelease.translations.map((t) => (
                                        <Link key={t.prId} to={getPressReleaseLink(pressRelease.companyId, t.prId)}>
                                            <img
                                                alt={t.language}
                                                title={t.language}
                                                src={flagPath(mapLanguageToCountry(t.language))}
                                                height={18}
                                                style={{ marginLeft: 14 }}
                                            />
                                        </Link>
                                    ))}
                                </List>
                            </Box>
                        </Box>
                        <h1>{pressRelease.title}</h1>

                        <Box
                            sx={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                gap: '4px',
                                alignItems: 'center',
                            }}
                        >
                            <ClockIcon size={16} />
                            {formatDateYYYYMMDDhhmmss(publishDate)}
                        </Box>

                        <Box sx={{ marginTop: 2 }}>
                            <PressReleaseRelatedTransactionsTags
                                relatedTransactions={pressRelease.relatedTransactions}
                            />
                        </Box>

                        <Box
                            sx={{
                                marginTop: 4,
                                ol: { listStylePosition: 'inside' },
                                ul: { listStylePosition: 'inside' },
                            }}
                            dangerouslySetInnerHTML={{ __html: bodyContent }}
                        />

                        <RelatedTransactions transactions={pressRelease.relatedTransactions} />

                        {attachments.length > 0 && (
                            <div>
                                <List subheader={<ListSubheader sx={{ paddingLeft: 0 }}>Länkade filer</ListSubheader>}>
                                    {attachments.map(({ caption, url }) => (
                                        <ListItemButton
                                            key={url}
                                            component='a'
                                            href={url}
                                            target='_blank'
                                            sx={{ paddingLeft: 0 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '36px' }}>
                                                <FileIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={caption || url} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </div>
                        )}
                    </PaperWrapper>
                </Grid>
                <Grid size={{ md: 4 }}>
                    <CompanyPressReleases companyId={pressRelease.companyId} currentPrId={pressRelease.id} />
                </Grid>
            </Grid>
        </>
    )
}
